/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-10 10:33:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import {
  Card,
  TextStyle,
  Button,
  ButtonGroup,
  Page,
  Layout,
  DisplayText,
  Grid,
  Banner,
  SkeletonDisplayText,
  SkeletonBodyText,
  SkeletonPage,
  Icon
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { DuplicateMinor, CircleTickMajor } from '@shopify/polaris-icons';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';

import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { trackFSEvent } from 'src/integrations/fullStory';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import EnableBoostCore from 'src/assets/images/recommendation/enable-boost-core.png';
import AddAppBlock from 'src/assets/images/recommendation/add-app-block.png';
import { URL } from 'src/constants/links';
import { ComposedProps } from './placement.type';

import EnhancePlacementWarningModal from 'src/components/views/recommendation/widget/item/enhance-placement-warning-modal';

class Placement extends Component<ComposedProps> {
  state = {
    enableBoostCoreVideo: false,
    addAppBlockVideo: false,
    clickedOnImAllDone: false,
    showWarningModal: false
  };

  componentWillReceiveProps(nextProps) {
    const { checkingEnableBoostCore, isEnableBoostCore, isEnableAppBlock } = nextProps;
    const { checkingEnableBoostCore: prevCheckingEnableBoostCore } = this.props;
    const { clickedOnImAllDone } = this.state;

    if (clickedOnImAllDone && prevCheckingEnableBoostCore && !checkingEnableBoostCore) {
      // Display warning modal if missing Enable boost core OR Add widget as App block
      if (!isEnableBoostCore || !isEnableAppBlock) {
        this.setState({ showWarningModal: true, clickedOnImAllDone: false });
      } else {
        this.setState({ clickedOnImAllDone: false });
        // push to theme setup page in shopify admin
        // window.open(url, '_blank', 'noopener,noreferrer');
        this.props.handleCloseFullscreenPage();
      }
    }
  }
  render() {
    const {
      i18n,
      handleCloseFullscreenPage,
      saving,
      checkingEnableBoostCore,
      isEnableBoostCore,
      isEnableAppBlock,
      widgetId,
      isCreateFlow,
      versionOS
    } = this.props;
    const { enableBoostCoreVideo, addAppBlockVideo } = this.state;

    const { showWarningModal } = this.state;

    const codeSnipped01 = `{% comment %} PLEASE DO NOT EDIT THIS FILE {% endcomment %}
{% case type %}
{% when 'product-filter' %}
<div class="boost-sd__product-filter-fallback">
<div class="boost-sd__filter-block"></div> </div>
{% when 'recommendation' %}
<div id="boost-sd-widget-{{- widgetId -}}"></div>
{% endcase %}`;

    const codeSnipped02 = `{% render 'boost-sd-app', type: 'recommendation', widgetId: 
'Replace widgetId here' %}`;

    if (versionOS === 0) {
      return (
        <SkeletonPage primaryAction fullWidth={false}>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
          </Layout>
        </SkeletonPage>
      );
    }

    return (
      <div className="BC-Page BC-Page__RecommendationWidgetItem">
        <FullscreenBar
          onAction={handleCloseFullscreenPage}
          backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
          title={i18n.translate(`AdminHTML.Recommendation.WidgetItem.doneTitle`)}
          actions={
            <ButtonGroup>
              <Button primary onClick={this.handleCompleteSetup} loading={saving}>
                {i18n.translate('AdminHTML.Recommendation.WidgetItem.complete')}
              </Button>
            </ButtonGroup>
          }
          progress={isCreateFlow ? 100 : -1}
        />
        <div style={{ marginTop: '2rem' }}>
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <div className="setup-content">
                    <div className="setup-content-title">
                      <DisplayText size="large">
                        {i18n.translate('AdminHTML.Recommendation.WidgetItem.placement_Label')}
                      </DisplayText>
                    </div>
                    {versionOS === 2 ? (
                      <div className="tab-content">
                        <div className="tab-content-title">
                          <DisplayText size="small">
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Title'
                              )}
                            </TextStyle>
                          </DisplayText>
                        </div>
                        <div className="tab-content-content">
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_Content'
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableBoostCore ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <Button primary onClick={this.enableBoostScore}>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_Action'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
                              <div className="BC-Iframe-Container">
                                {enableBoostCoreVideo ? (
                                  <iframe
                                    title={i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_IframeTitle'
                                    )}
                                    src={`${URL['Link_V2_Recommendation_ManageWidget_Video']}&start=276&autoplay=1`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen></iframe>
                                ) : (
                                  <img
                                    src={EnableBoostCore}
                                    alt="enable-boost-core"
                                    style={{ width: '100%' }}
                                    onClick={() => this.setEnableVideo('enableBoostCoreVideo')}
                                  />
                                )}
                              </div>
                            </Grid.Cell>
                          </Grid>
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step2_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step2_Content',
                                  {
                                    nt: (
                                      <TextStyle variation="strong">
                                        {widgetId}
                                        <span className="copy">
                                          <Button
                                            icon={DuplicateMinor}
                                            plain
                                            onClick={() => this.copyClipBoard(widgetId)}
                                          />
                                        </span>
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableAppBlock ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step2_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <Button primary onClick={this.addAppBlock}>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step2_Action'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
                              <div className="BC-Iframe-Container">
                                {addAppBlockVideo ? (
                                  <iframe
                                    title={i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_IframeTitle'
                                    )}
                                    src={`${URL['Link_V2_Recommendation_ManageWidget_Video']}&start=296&autoplay=1`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen></iframe>
                                ) : (
                                  <img
                                    src={AddAppBlock}
                                    alt="add-app-block"
                                    style={{ width: '100%' }}
                                    onClick={() => this.setEnableVideo('addAppBlockVideo')}
                                  />
                                )}
                              </div>
                            </Grid.Cell>
                          </Grid>
                        </div>
                      </div>
                    ) : (
                      <div className="tab-content">
                        <div className="tab-content-content">
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step1_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step1_Content',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step1_Content_t1'
                                        )}
                                      </TextStyle>
                                    ),
                                    t2: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step1_Content_t2'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableBoostCore ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <Button primary onClick={this.enableBoostScore}>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_Store2_Step1_Action'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
                              <div className="BC-Iframe-Container">
                                {enableBoostCoreVideo ? (
                                  <iframe
                                    title={i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placement_IframeTitle'
                                    )}
                                    src={`${URL['Link_V2_Recommendation_ManageWidget_Video']}&start=276&autoplay=1`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen></iframe>
                                ) : (
                                  <img
                                    src={EnableBoostCore}
                                    alt="enable-boost-core"
                                    style={{ width: '100%' }}
                                    onClick={() => this.setEnableVideo('enableBoostCoreVideo')}
                                  />
                                )}
                              </div>
                            </Grid.Cell>
                          </Grid>
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Content01',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Content01_t1'
                                        )}
                                      </TextStyle>
                                    ),
                                    t2: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Content01_t2'
                                        )}
                                      </TextStyle>
                                    ),
                                    t3: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Content01_t3'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step2_Content02',
                                  {
                                    nt: (
                                      <TextStyle variation="strong">boost-sd-app.liquid</TextStyle>
                                    )
                                  }
                                )}
                              </p>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                              <img
                                alt="gif-create-new-snippet"
                                src="https://d33v4339jhl8k0.cloudfront.net/docs/assets/590652f62c7d3a057f88b05d/images/643e1e7d7133de139103ec27/file-3d22gAnBkp.gif"
                                style={{ width: '100%' }}
                              />
                            </Grid.Cell>
                          </Grid>
                          <div>
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step3_Title'
                              )}
                            </TextStyle>
                            <p>
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step3_Content',
                                {
                                  nt: <TextStyle variation="strong">boost-sd-app.liquid</TextStyle>
                                }
                              )}
                            </p>
                            <div className="code">
                              <Button
                                icon={DuplicateMinor}
                                plain
                                onClick={() => this.copyClipBoard(codeSnipped01)}
                              />
                              <p>{`{% comment %} PLEASE DO NOT EDIT THIS FILE {% endcomment %}`}</p>
                              <p>{`{% case type %}`}</p>
                              <p>{`{% when 'product-filter' %}`}</p>
                              <p>{`<div class="boost-sd__product-filter-fallback">`}</p>
                              <p>{`<div class="boost-sd__filter-block"></div> </div>`}</p>
                              <p>{`{% when 'recommendation' %}`}</p>
                              <p>{`<div id="boost-sd-widget-{{- widgetId -}}"></div>`}</p>
                              <p>{`{% endcase %}`}</p>
                            </div>
                          </div>
                          <div>
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Title'
                              )}
                            </TextStyle>
                            <p>
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Content01'
                              )}
                            </p>
                            <div className="code">
                              <Button
                                icon={DuplicateMinor}
                                plain
                                onClick={() => this.copyClipBoard(codeSnipped02)}
                              />
                              <p>{`{% render 'boost-sd-app', type: 'recommendation', widgetId: 'Replace widgetId here' %}`}</p>
                            </div>
                            <p style={{ marginBottom: '0.5rem' }}>
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Content02',
                                {
                                  t1: (
                                    <TextStyle variation="strong">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Content02_t1'
                                      )}
                                    </TextStyle>
                                  ),
                                  t2: (
                                    <TextStyle variation="strong">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Content02_t2'
                                      )}
                                    </TextStyle>
                                  ),
                                  t3: (
                                    <TextStyle variation="strong">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Content02_t3'
                                      )}
                                    </TextStyle>
                                  )
                                }
                              )}
                            </p>
                            <Banner status="info">
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Banner01',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Banner01_t1'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                                <br />
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placement_Store1_Step4_Banner02'
                                )}
                              </p>
                              <p>
                                <TextStyle variation="strong">{`{% render 'boost-sd-app', type: 'recommendation', widgetId: 'homepage-326800' %}`}</TextStyle>
                              </p>
                            </Banner>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </Layout.Section>
            </Layout>

            <EnhancePlacementWarningModal
              active={showWarningModal}
              addWidgetNow={this.handleCloseModal}
              doItLater={handleCloseFullscreenPage}
            />
          </Page>
        </div>
      </div>
    );
  }

  copyClipBoard = (copyText: string) => {
    const { i18n } = this.props;
    try {
      const inputText = document.createElement('textarea');
      inputText.value = copyText;
      document.body.appendChild(inputText);
      inputText.select();

      document.execCommand('copy');
      document.body.removeChild(inputText);
      showSABToast(i18n.translate('AdminHTML.Common.Action.copied', { t: copyText }), false);
    } catch (error) {}
  };

  enableBoostScore = () => {
    const { embedDeepLink, themeName } = this.props;
    if (!_.isEmpty(embedDeepLink)) {
      window.open(embedDeepLink, '_blank', 'noopener,noreferrer');
      // Track Full Story event
      trackFSEvent('Enable Boost Core', {
        theme: themeName
      });
    }
  };

  addAppBlock = () => {
    const { appBlockDeepLink, themeName } = this.props;
    if (!_.isEmpty(appBlockDeepLink)) {
      window.open(appBlockDeepLink, '_blank', 'noopener,noreferrer');
      // Track Full Story event
      trackFSEvent('Add App Block', {
        theme: themeName
      });
    }
  };

  // Handle setup action: complete
  handleCompleteSetup = async () => {
    const { widgetId } = this.props;

    // Turn on checking placement process
    this.setState({ clickedOnImAllDone: true });

    // Get enable boost core on live theme with specific widget
    const queries = { widgetId };
    await this.props.checkEnableBoostCore(queries);
  };

  // Click on Add widget now (remain on the same page) from warning modal
  handleCloseModal = () => {
    this.setState({ showWarningModal: false, clickedOnImAllDone: false });
  };

  setEnableVideo = videoType => {
    this.setState({ [videoType]: true });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Check enable boost core on live theme
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    saving: _.get(state.recommendation, 'saving', false),
    checkingEnableBoostCore: _.get(state.recommendation, 'checkingEnableBoostCore', false),
    isEnableBoostCore: _.get(state.recommendation, 'isEnableBoostCore', false),
    isEnableAppBlock: _.get(state.recommendation, 'isEnableAppBlock', false),
    appBlockDeepLink: _.get(state.recommendation, 'appBlockDeepLink', ''),
    embedDeepLink: _.get(state.recommendation, 'embedDeepLink', ''),
    widgetId: _.get(state.recommendation, 'widgetId', ''),
    recommendationWidget: _.get(state.recommendation, 'recommendationWidget', {}),
    themeName: _.get(state.themeSetting, 'themeName', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Placement));
